import { useState, useRef, useEffect } from 'react';
import { Columns, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface ColumnDefinition {
  id: string;
  label: string;
  group: string;
}

interface ColumnToggleProps {
  columns: ColumnDefinition[];
  visibleColumns: string[];
  onToggle: (columnId: string) => void;
  onToggleAll: (show: boolean) => void;
}

export function ColumnToggle({ columns, visibleColumns, onToggle, onToggleAll }: ColumnToggleProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Group columns by their group property
  const groupedColumns = columns.reduce((acc, col) => {
    if (!acc[col.group]) acc[col.group] = [];
    acc[col.group].push(col);
    return acc;
  }, {} as Record<string, ColumnDefinition[]>);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md',
          'border border-border bg-card hover:bg-accent',
          'text-muted-foreground hover:text-foreground transition-colors'
        )}
        title="Toggle columns"
      >
        <Columns className="w-4 h-4" />
        <span className="hidden sm:inline">Columns</span>
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-1 z-50 w-64 max-h-96 overflow-auto rounded-lg border border-border bg-card shadow-lg">
          {/* Header with All/None buttons */}
          <div className="flex items-center justify-between p-2 border-b border-border">
            <span className="text-sm font-medium text-foreground">Columns</span>
            <div className="flex gap-1">
              <button
                onClick={() => onToggleAll(true)}
                className="px-2 py-0.5 text-xs rounded bg-muted hover:bg-accent"
              >
                All
              </button>
              <button
                onClick={() => onToggleAll(false)}
                className="px-2 py-0.5 text-xs rounded bg-muted hover:bg-accent"
              >
                None
              </button>
            </div>
          </div>

          {/* Grouped columns */}
          <div className="p-2 space-y-3">
            {Object.entries(groupedColumns).map(([group, cols]) => (
              <div key={group}>
                <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-1">
                  {group}
                </h4>
                <div className="space-y-0.5">
                  {cols.map(col => (
                    <label
                      key={col.id}
                      className="flex items-center gap-2 px-2 py-1 rounded hover:bg-accent cursor-pointer"
                    >
                      <div className={cn(
                        'w-4 h-4 rounded border flex items-center justify-center',
                        visibleColumns.includes(col.id)
                          ? 'bg-primary border-primary'
                          : 'border-border'
                      )}>
                        {visibleColumns.includes(col.id) && (
                          <Check className="w-3 h-3 text-primary-foreground" />
                        )}
                      </div>
                      <input
                        type="checkbox"
                        checked={visibleColumns.includes(col.id)}
                        onChange={() => onToggle(col.id)}
                        className="sr-only"
                      />
                      <span className="text-sm text-foreground">{col.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
